/**
 * Wallet Transfer Modal
 *
 * 현물 지갑 → 카피트레이딩 지갑 전송 모달
 * - 잔액 조회
 * - 금액 입력 및 검증
 * - 전송 실행
 *
 * @see coin-server/apps/api/src/wallet/handlers/wallet-transfer.handler.ts
 */

import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, InputNumber, message, Spin } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIsTablet } from '@utils/CalVW.tsx';
import { getBigNumber } from '@utils/getBigNumber';
import { formatDecimal } from '@utils/formatDecimal';
import { SwapOutlined } from '@ant-design/icons';

import { walletTransferApi } from '../../../api/wallet';

type TransferType = 'toCopy' | 'toSpot';

interface WalletTransferModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export const WalletTransferModal: React.FC<WalletTransferModalProps> = ({
  open,
  setOpen,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const [type, setType] = useState<TransferType>('toCopy');
  const [transferAmount, setTransferAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // 지갑 잔액 상태
  const [spotBalance, setSpotBalance] = useState<number>(0);
  const [copyBalance, setCopyBalance] = useState<number>(0);

  // 모달 열릴 때 잔액 조회
  useEffect(() => {
    if (open) {
      loadBalances();
      setTransferAmount(0);
    }
  }, [open]);

  const loadBalances = async () => {
    setBalanceLoading(true);
    try {
      const balances = await walletTransferApi.getBalances();
      setSpotBalance(balances.spot);
      setCopyBalance(balances.copy);
    } catch (error: any) {
      message.error(error.message || t('balanceLoadError'));
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleTransfer = async (type: TransferType) => {
    if (transferAmount <= 0) {
      message.error(t('transferAmountRequired'));
      return;
    }

    // 현물지갑에서 카피지갑으로 옮길때 현물지갑의 잔액을 검사
    if (type === 'toCopy' && transferAmount > spotBalance) {
      message.error(t('insufficientSpotBalance'));
      return;
    }

    // 카피지갑에서 헌물지갑으로 옮길때 카피지갑의 잔액을 검사
    if (type === 'toSpot' && transferAmount > copyBalance) {
      message.error(t('insufficientCopyBalance'));
      return;
    }

    setIsLoading(true);
    try {
      const result =
        type === 'toCopy'
          ? await walletTransferApi.transferToCopy(transferAmount)
          : await walletTransferApi.transferToSpot(transferAmount);

      message.success(
        type === 'toCopy'
          ? t('toCopySuccessTransfer', { amount: transferAmount })
          : t('toSpotSuccessTransfer', { amount: transferAmount })
      );

      // 잔액 업데이트
      setSpotBalance(result.data.fromBalance.after);
      setCopyBalance(result.data.toBalance.after);

      setTransferAmount(0);

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        setOpen(false);
      }, 1000);
    } catch (error: any) {
      message.error(error.message || t('transferFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaxClick = (type: TransferType) => {
    if (type === 'toCopy') {
      setTransferAmount(spotBalance);
    } else {
      setTransferAmount(copyBalance);
    }
  };

  return (
    <Modal
      open={open}
      width={isTablet ? '90%' : '480px'}
      centered={true}
      closable={true}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel} disabled={isLoading}>
          {t('cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={() => handleTransfer(type)}
        >
          {t('transfer')}
        </Button>,
      ]}
      title={t('walletTransfer')}
      className={'p2pmodal' + (theme === 'dark' ? ' dark' : '')}
    >
      <ModalContents theme={theme}>
        {balanceLoading ? (
          <LoadingContainer>
            <Spin size="large" />
            <LoadingText theme={theme}>{t('loadingBalances')}</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            {/* 현재 잔액 정보 */}
            {type === 'toCopy' ? (
              <BalanceSection>
                <BalanceCard theme={theme}>
                  <BalanceLabel theme={theme}>{t('spotWallet')}</BalanceLabel>
                  <BalanceValue theme={theme}>
                    {formatDecimal(spotBalance, 2)} USDT
                  </BalanceValue>
                </BalanceCard>

                <SwapIcon
                  onClick={() =>
                    setType((prev) => (prev === 'toCopy' ? 'toSpot' : 'toCopy'))
                  }
                >
                  <SwapOutlined style={{ fontSize: 20, color: '#3b82f6' }} />
                </SwapIcon>

                <BalanceCard theme={theme} highlight>
                  <BalanceLabel theme={theme}>
                    {t('copyTradingWallet')}
                  </BalanceLabel>
                  <BalanceValue theme={theme}>
                    {formatDecimal(copyBalance, 2)} USDT
                  </BalanceValue>
                </BalanceCard>
              </BalanceSection>
            ) : (
              <BalanceSection>
                <BalanceCard theme={theme}>
                  <BalanceLabel theme={theme}>
                    {t('copyTradingWallet')}
                  </BalanceLabel>
                  <BalanceValue theme={theme}>
                    {formatDecimal(copyBalance, 2)} USDT
                  </BalanceValue>
                </BalanceCard>

                <SwapIcon
                  onClick={() =>
                    setType((prev) => (prev === 'toCopy' ? 'toSpot' : 'toCopy'))
                  }
                >
                  <SwapOutlined style={{ fontSize: 20, color: '#3b82f6' }} />
                </SwapIcon>

                <BalanceCard theme={theme} highlight>
                  <BalanceLabel theme={theme}>{t('spotWallet')}</BalanceLabel>
                  <BalanceValue theme={theme}>
                    {formatDecimal(spotBalance, 2)} USDT
                  </BalanceValue>
                </BalanceCard>
              </BalanceSection>
            )}

            <Divider theme={theme} />

            {/* 전송 금액 입력 */}
            <FormSection>
              <FormLabel theme={theme}>
                {t('transferAmountLabel')}
                <MaxButton onClick={() => handleMaxClick(type)} theme={theme}>
                  {t('maxAmount')}
                </MaxButton>
              </FormLabel>
              <StyledInputNumber
                min={0}
                max={type === 'toCopy' ? spotBalance : copyBalance}
                value={transferAmount}
                onChange={(value) => setTransferAmount(Number(value) || 0)}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value?.replace(/\$\s?|(,*)/g, '') as any}
                style={{ width: '100%' }}
                placeholder={t('transferAmountPlaceholder')}
              />
              <FormDesc theme={theme}>
                {t('availableAmount')}:{' '}
                {formatDecimal(
                  type === 'toCopy' ? spotBalance : copyBalance,
                  2
                )}{' '}
                USDT
              </FormDesc>
            </FormSection>

            <Divider theme={theme} />

            {/* 전송 후 예상 잔액 */}
            <SummarySection theme={theme}>
              <SummaryRow>
                <SummaryLabel theme={theme}>
                  {t('spotWalletBalance')}
                </SummaryLabel>
                <SummaryValue theme={theme}>
                  {type === 'toCopy'
                    ? formatDecimal(
                        getBigNumber(spotBalance)
                          .minus(transferAmount)
                          .toNumber(),
                        2
                      )
                    : formatDecimal(
                        getBigNumber(spotBalance)
                          .plus(transferAmount)
                          .toNumber(),
                        2
                      )}{' '}
                  USDT
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel theme={theme}>
                  {t('copyWalletBalance')}
                </SummaryLabel>
                <SummaryValue theme={theme} highlight>
                  {type === 'toCopy'
                    ? formatDecimal(
                        getBigNumber(copyBalance)
                          .plus(transferAmount)
                          .toNumber(),
                        2
                      )
                    : formatDecimal(
                        getBigNumber(copyBalance)
                          .minus(transferAmount)
                          .toNumber(),
                        2
                      )}{' '}
                  USDT
                </SummaryValue>
              </SummaryRow>
            </SummarySection>

            {/* 안내 문구 */}
            <NoticeBox theme={theme}>
              • {t('walletTransferNotice1')}
              <br />• {t('walletTransferNotice2')}
              <br />• {t('walletTransferNotice3')}
            </NoticeBox>
          </>
        )}
      </ModalContents>
    </Modal>
  );
};

// ==========================================
// Styled Components
// ==========================================

const ModalContents = styled.div<{ theme?: string }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
  background-color: ${({ theme }) => (theme === 'dark' ? '#1a1a1a' : '#fff')};
  border-radius: 10px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 16px;
`;

const LoadingText = styled.div<{ theme?: string }>`
  font-size: 14px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
`;

const BalanceSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const BalanceCard = styled.div<{ highlight?: boolean; theme?: string }>`
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ highlight, theme }) =>
    highlight
      ? theme === 'dark'
        ? '#1e3a5f'
        : '#dbeafe'
      : theme === 'dark'
        ? '#222'
        : '#f5f5f5'};
  border: 2px solid
    ${({ highlight }) => (highlight ? '#3b82f6' : 'transparent')};
`;

const BalanceLabel = styled.div<{ theme?: string }>`
  font-size: 13px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
  margin-bottom: 8px;
`;

const BalanceValue = styled.div<{ theme?: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => (theme === 'dark' ? '#e5e5e5' : '#1a1a1a')};
`;

const SwapIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(59, 130, 246, 0.1);
`;

const Divider = styled.div<{ theme?: string }>`
  height: 1px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#333' : '#e0e0e0')};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.div<{ theme?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => (theme === 'dark' ? '#e5e5e5' : '#1a1a1a')};
`;

const MaxButton = styled.span<{ theme?: string }>`
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 4px;
  background-color: ${({ theme }) =>
    theme === 'dark' ? '#1e3a5f' : '#dbeafe'};
  transition: all 0.2s;

  &:hover {
    background-color: #3b82f6;
    color: white;
  }
`;

const FormDesc = styled.div<{ theme?: string }>`
  font-size: 13px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
  opacity: 0.9;
`;

const StyledInputNumber = styled(InputNumber)`
  height: 40px;
  font-size: 16px;
  font-weight: 600;

  .ant-input-number-input {
    height: 100%;
    font-weight: 600;
  }
`;

const SummarySection = styled.div<{ theme?: string }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#222' : '#f5f5f5')};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SummaryLabel = styled.div<{ theme?: string }>`
  font-size: 14px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
`;

const SummaryValue = styled.div<{ highlight?: boolean; theme?: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ highlight, theme }) =>
    highlight ? '#3b82f6' : theme === 'dark' ? '#e5e5e5' : '#1a1a1a'};
`;

const NoticeBox = styled.div<{ theme?: string }>`
  padding: 12px;
  border-radius: 8px;
  background-color: ${({ theme }) =>
    theme === 'dark' ? '#1e3a5f' : '#dbeafe'};
  color: ${({ theme }) => (theme === 'dark' ? '#93c5fd' : '#1e40af')};
  font-size: 12px;
  line-height: 1.8;
  border-left: 4px solid #3b82f6;
`;
